import { Concern, useExplore } from "@/components/explore/ExploreContext";
import { PlayDetail } from "@/components/playlist/PlayDetail";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import _ from "lodash";
import React, { FunctionComponent, useMemo, useState } from "react";

export type TableCellFormatter = (params: {
  zoneName: string;
  timeLabel: string;
  value: number;
  rowIndex: number;
  colIndex: number;
}) => {
  display: React.ReactNode;
  tooltip?: string;
};

function ConcernDisplay({ concern }: { concern: Concern }) {
  return <PlayDetail playId={concern.play_id} />;
}

interface TableViewProps {
  customCellFormatter?: TableCellFormatter;
  onCellClick?: (
    zoneId: number,
    interval: number,
    zoneName: string,
    timeLabel: string,
    zoneIds?: number[]
  ) => void;
}

export const TableView: FunctionComponent<TableViewProps> = ({
  customCellFormatter,
  onCellClick,
}) => {
  const { zones: rawZones, times, concerns, zoneType } = useExplore();
  const [open, setOpen] = useState(false);
  const [selectedConcern, setSelectedConcern] = useState<Concern | null>(null);

  // Reverse all zones EXCEPT doors and APM (doors come in CSV order, APM comes in station sequence order, others come in reverse natural sort)
  const zones =
    zoneType === "door" || zoneType === "apm"
      ? rawZones
      : _.clone(rawZones).reverse();

  const { min, max } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;

    zones.forEach((zone) => {
      zone.values.forEach((value) => {
        min = Math.min(min, value);
        max = Math.max(max, value);
      });
    });

    return { min, max };
  }, [zones]);

  // Create concern lookup similar to Heatmap
  const conLookup = useMemo(() => {
    const lookup: { [key: string]: Concern } = {};
    if (concerns && concerns.length > 0) {
      concerns.forEach((c) => {
        const adjustedZoneIndex = zones.length - 1 - c.zone_index;
        lookup[`${adjustedZoneIndex}-${c.time_index}`] = c;
      });
    }
    return lookup;
  }, [concerns, zones.length]);

  const formatValue = (target: number, rowIndex: number, colIndex: number) => {
    const concernKey = `${rowIndex}-${colIndex}`;
    const hasConcern = concernKey in conLookup;
    const zone = zones[rowIndex];
    const timeLabel = times[colIndex];

    let ratio = 0;
    const sat = "80";
    const lum = "50";
    if (max != min && target > 0) {
      ratio = (target - min) / (max - min);
    }
    const hue = Math.max(30, (1 - ratio) * 120);
    const color = `hsl(${hue}, ${sat}%, ${lum}%)`;

    const bgOpacity = target === 0 ? 0 : Math.max(0.05, ratio * 0.25);
    const bgColor =
      target === 0 ? "transparent" : `hsla(${hue}, 70%, 50%, ${bgOpacity})`;

    let displayContent: React.ReactNode;
    let tooltipContent: string | undefined;

    if (customCellFormatter) {
      const formatted = customCellFormatter({
        zoneName: zone.name,
        timeLabel,
        value: target,
        rowIndex,
        colIndex,
      });
      tooltipContent = formatted.tooltip;

      if (target === 0) {
        displayContent = (
          <span className={"text-gray-600"}>{formatted.display}</span>
        );
      } else {
        displayContent = <span style={{ color }}>{formatted.display}</span>;
      }
    } else {
      if (target === 0) {
        displayContent = <span className={"text-gray-600"}>{target}</span>;
      } else {
        displayContent = (
          <span style={{ color }}>{target.toLocaleString()}</span>
        );
      }
    }

    const content = (
      <span
        className={`${hasConcern ? "bg-red-900 px-2 py-1 rounded cursor-pointer" : "px-2 py-1 rounded cursor-pointer"}`}
        style={{
          backgroundColor: hasConcern ? undefined : bgColor,
        }}
        onClick={() => {
          if (hasConcern) {
            setSelectedConcern(conLookup[concernKey]);
            setOpen(true);
          } else if (zone.id && onCellClick) {
            onCellClick(zone.id, colIndex, zone.name, timeLabel, zone.zone_ids);
          }
        }}
      >
        {displayContent}
      </span>
    );

    if (tooltipContent) {
      return (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>{content}</TooltipTrigger>
            <TooltipContent>
              <div dangerouslySetInnerHTML={{ __html: tooltipContent }} />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return content;
  };

  return (
    <>
      <div className="flex w-full overflow-hidden text-xs font-mono rounded-lg">
        <div className="flex w-32 overflow-hidden">
          {/* Fixed Column Table */}
          <table className="w-full whitespace-nowrap">
            <thead>
              <tr>
                <th className="sticky top-0 text-left p-2 bg-slate-800/50 text-slate-400 text-xs rounded-tl-lg">
                  Name
                </th>
              </tr>
            </thead>
            <tbody>
              {zones.map((zone, i) => (
                <tr key={i}>
                  <td className="py-1 px-2 h-8">{zone.name}</td>
                </tr>
              ))}
              <tr className="bg-slate-800 font-bold">
                <td className="py-1 px-2 h-8 rounded-bl-lg">Total</td>
              </tr>
              {/* Spacer */}
              <tr className={"h-12"}></tr>
            </tbody>
          </table>
        </div>
        <div className="flex-1 overflow-x-scroll">
          {/* Scrollable Table */}
          <table className="w-full whitespace-nowrap">
            <thead>
              <tr>
                {zones[0].values.map((_, i) => {
                  return (
                    <th className="table-header-cell table-cell-single" key={i}>
                      {times[i]}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {zones.map((zone, rowIndex) => (
                <tr key={rowIndex}>
                  {zone.values.map((value, colIndex) => (
                    <td
                      className="table-data-cell table-cell-single"
                      key={colIndex}
                    >
                      {formatValue(value, rowIndex, colIndex)}
                    </td>
                  ))}
                </tr>
              ))}
              {/* Totals Row */}
              <tr className="bg-slate-800 font-bold">
                {zones[0].values.map((foo, colIndex) => (
                  <td className="table-data-cell" key={colIndex}>
                    {parseInt(
                      _.sumBy(zones, (zone) => zone.values[colIndex])
                    ).toLocaleString("en-US")}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <div className="flex w-16 overflow-hidden">
          {/* Fixed Column Table */}
          <table className="w-full whitespace-nowrap">
            <thead>
              <tr>
                <th className="sticky top-0 text-left p-2 bg-slate-800/50 text-slate-400 text-xs rounded-tr-lg">
                  Totals
                </th>
              </tr>
            </thead>
            <tbody>
              {zones.map((zone, i) => (
                <tr key={i}>
                  <td className="py-1 px-2 h-8 dark:text-slate-400">
                    {_.sum(zone.values).toLocaleString()}
                  </td>
                </tr>
              ))}
              <tr className="">
                <td className="py-1 px-2 h-8 rounded-br-lg"></td>
              </tr>
              {/* Spacer */}
              <tr className={"h-12"}></tr>
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alert Details</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[calc(100vh-250px)]">
            {selectedConcern && <ConcernDisplay concern={selectedConcern} />}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};
